/* ============================================================
   Falconclaw — Sticky Tab Bar
   ------------------------------------------------------------
   Pins the product-page tab nav (Propaganda / Cables / Specs /
   FAQ / What's in the Box, and every other product template that
   uses the Impact "tabs" section) to the top of the viewport while
   a long tab is being read, so customers never lose the nav.

   WHY JS AND NOT CSS position:sticky:
   Impact renders the tab row as a shadow-DOM <slot part="tab-list">
   inside a CSS GRID, and the theme sets overflow-y:auto on <body>
   for its image-zoom feature. Both independently break position:
   sticky on the tab row (verified on the live page). So we build a
   lightweight proxy bar in the light DOM and toggle position:fixed
   on scroll, which is immune to both issues.

   RELIABILITY:
   - Fully wrapped in try/catch. If anything fails, the native tabs
     are left untouched and the page behaves exactly as before.
   - The original tab buttons are only hidden AFTER the proxy bar is
     successfully built, so a failure can never leave a tab-less page.
   - Desktop only (>=700px). Below that, Impact shows native
     accordions and this script no-ops.
   ============================================================ */
(function () {
  'use strict';

  var MIN_WIDTH = 700;

  function px(el, varName, fallback) {
    try {
      var probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;height:var(' + varName + ',0px)';
      el.appendChild(probe);
      var h = probe.getBoundingClientRect().height;
      probe.remove();
      return h || fallback || 0;
    } catch (e) { return fallback || 0; }
  }

  function initSection(section) {
    try {
      var host = section.querySelector('x-tabs');
      if (!host) return;
      var container = host.closest('.tabs-container') || host.parentElement;
      var realButtons = Array.prototype.slice.call(host.querySelectorAll('.tabs-nav__button'));
      if (realButtons.length < 2) return; // nothing to pin

      // ---- build the proxy bar (light DOM, reliable for position:fixed) ----
      var bar = document.createElement('div');
      bar.className = 'fc-stabs';
      bar.setAttribute('role', 'tablist');

      var inner = document.createElement('div');
      inner.className = 'fc-stabs__inner';
      bar.appendChild(inner);

      realButtons.forEach(function (rb, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'fc-stabs__btn';
        b.textContent = (rb.textContent || '').trim();
        b.setAttribute('aria-selected', rb.getAttribute('aria-selected') === 'true' ? 'true' : (i === 0 ? 'true' : 'false'));
        b.addEventListener('click', function () {
          rb.click();
          // keep the page anchored to the tabs after a switch so the
          // reader doesn't get thrown around by content-height changes
          var topNow = section.getBoundingClientRect().top;
          if (topNow < OFFSET) {
            window.scrollBy(0, topNow - OFFSET);
          }
        });
        inner.appendChild(b);
      });

      // placeholder keeps layout height when the original row is hidden
      var ph = document.createElement('div');
      ph.className = 'fc-stabs__ph';
      container.insertBefore(ph, host);
      container.insertBefore(bar, host);

      // hide the native (slotted) row ONLY now that the proxy exists
      section.classList.add('fc-stabs-active');

      var OFFSET = px(document.body, '--sticky-area-height', 0);
      var barH = bar.offsetHeight || 56;

      function sync() {
        realButtons.forEach(function (rb, i) {
          if (inner.children[i]) {
            inner.children[i].setAttribute('aria-selected', rb.getAttribute('aria-selected') === 'true' ? 'true' : 'false');
          }
        });
      }

      // keep the proxy inner row aligned to the x-tabs CONTENT column
      // (e.g. 1000px for content_size:medium), in BOTH flow and fixed
      // states, so the buttons never shift horizontally when pinning.
      function alignInner() {
        try {
          var hostRect = host.getBoundingClientRect();
          // host width = the tab content column width; left = its viewport x
          inner.style.width = Math.round(hostRect.width) + 'px';
          if (bar.classList.contains('fc-stabs--fixed')) {
            inner.style.marginLeft = Math.round(hostRect.left) + 'px';
          } else {
            inner.style.marginLeft = 'auto';
            inner.style.marginRight = 'auto';
          }
        } catch (e) {}
      }

      function update() {
        if (window.innerWidth < MIN_WIDTH) {
          bar.classList.remove('fc-stabs--fixed');
          ph.style.height = '0px';
          return;
        }
        barH = bar.classList.contains('fc-stabs--fixed') ? barH : (bar.offsetHeight || barH);
        // anchor to the SECTION (stable across tab switches), not the inner
        var sRect = section.getBoundingClientRect();
        // pin while the section spans the offset line and there is still
        // section below the bar to scroll through
        var shouldFix = sRect.top <= OFFSET && sRect.bottom > OFFSET + barH + 8;
        if (shouldFix) {
          if (!bar.classList.contains('fc-stabs--fixed')) {
            ph.style.height = barH + 'px';
            bar.classList.add('fc-stabs--fixed');
          }
          bar.style.top = OFFSET + 'px';
        } else {
          if (bar.classList.contains('fc-stabs--fixed')) {
            bar.classList.remove('fc-stabs--fixed');
            ph.style.height = '0px';
            bar.style.top = '';
          }
        }
        alignInner();
      }

      // wire events
      realButtons.forEach(function (rb) {
        rb.addEventListener('click', function () { setTimeout(function () { sync(); update(); }, 30); });
      });
      try {
        new MutationObserver(sync).observe(host, { attributes: true, subtree: true, attributeFilter: ['aria-selected'] });
      } catch (e) {}
      try {
        new ResizeObserver(function () { OFFSET = px(document.body, '--sticky-area-height', 0); update(); }).observe(section);
      } catch (e) {}

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', function () { OFFSET = px(document.body, '--sticky-area-height', 0); barH = bar.offsetHeight || barH; update(); }, { passive: true });

      sync();
      update();
    } catch (err) {
      // any failure: leave native tabs as-is
      if (window.console) console.warn('[fc-sticky-tabs] init failed, native tabs left intact:', err);
    }
  }

  function boot() {
    var sections = document.querySelectorAll('.shopify-section--tabs');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
